import React, { useState } from 'react';
import { Building, Edit, Loader2, Plus, Trash2 } from 'lucide-react';
import { Company, User } from '../types';
import { EditCompanyForm } from './EditCompanyForm';
import { useUpdateCompany } from '@/core/api/queries/companies';
import { useDeleteCompany } from '@/core/api/queries/companies';
import { useCompanies } from '@/core/api/queries/companies';
import { useUsers } from '@/core/api/queries/users';

interface CompaniesTabProps {
  setShowCreateCompany: (show: boolean) => void;
}

export const CompaniesTab: React.FC<CompaniesTabProps> = ({ setShowCreateCompany }) => {
  const companies = useCompanies();
  const companiesData = (companies.data as Company[]) || [];
  const [editingCompany, setEditingCompany] = useState<string | null>(null);

  const users = useUsers();
  const usersData = (users.data as User[]) || [];
  const lspCompaniesData = companiesData.filter((company) => company.role === 'LSP');

  const updateCompany = useUpdateCompany();
  const deleteCompany = useDeleteCompany();

  const handleDeleteCompany = (companyId: string): void => {
    if (confirm('Delete this company and all its users?')) {
      deleteCompany.mutate(companyId);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Companies</h2>
        <button
          onClick={() => setShowCreateCompany(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus size={18} />
          <span>Add Company</span>
        </button>
      </div>

      {companies.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading companies...</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Company</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Contact</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Users</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Created</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lspCompaniesData.map((company) => (
                <tr key={company.id} className="border-b border-gray-100 hover:bg-gray-50">
                  {editingCompany === company.id ? (
                    <td colSpan={5} className="py-4 px-4">
                      <EditCompanyForm
                        company={company}
                        onSave={(data) => {
                          updateCompany.mutate({ id: company.id, data });
                          setEditingCompany(null);
                        }}
                        onCancel={() => setEditingCompany(null)}
                      />
                    </td>
                  ) : (
                    <>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building size={16} className="text-blue-600" />
                          </div>
                          <span className="font-medium text-gray-900">{company.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm">
                          <div className="text-gray-900">{company.email}</div>
                          {company.phone && <div className="text-gray-500">{company.phone}</div>}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600">
                          {usersData.filter((u) => u.companyId === company.id).length}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-500">
                          {new Date(company.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setEditingCompany(company.id)}
                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                          >
                            <Edit size={16} className="text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteCompany(company.id)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
